import { useMemo } from 'react';

import { useProductStockLocations } from '../hooks';
import type { ProductStockLocation } from '../model/productStockTypes';

export interface ProductLocationLookup {
  locations: ProductStockLocation[];
  isLoading: boolean;
  isError: boolean;
  /** Resolved location name, or the raw id itself when it isn't (yet) resolvable. */
  getLocationName: (locationId: string) => string;
}

/**
 * Fetches the tenant's fulfilment locations once (cached — see `useProductStockLocations`) and
 * exposes an id→name lookup for `ProductStockPanel`. Mirrors `features/inventory`'s own
 * `useLocationLookup` exactly — a local, catalog-owned copy per FSD's no-cross-feature-import rule.
 */
export function useProductLocationLookup(): ProductLocationLookup {
  const query = useProductStockLocations();
  const byId = useMemo(() => {
    const map = new Map<string, ProductStockLocation>();
    for (const location of query.data ?? []) {
      map.set(location.id, location);
    }
    return map;
  }, [query.data]);

  return {
    locations: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    getLocationName: (locationId: string) => byId.get(locationId)?.name ?? locationId,
  };
}
